import { PackageOpen, Plus, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { AddHeroAsinModal } from "@/components/shared/AddHeroAsinModal";
import { AddLandscapeModal } from "@/components/shared/AddLandscapeModal";
import { useAppData } from "@/contexts/AppDataContext";
import { toast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { productApi } from "@/resources/product";
import { handleApiError } from "@/utils";

type SkeletonVariant = "dashboard" | "products" | "rank-tracker" | "search-terms" | "brand-signals" | "generic";

interface EmptyBrandStateProps {
  pageName: string;
  description?: string;
  showLandscapeCta?: boolean;
  skeleton?: SkeletonVariant;
  keywordCta?: {
    label?: string;
    description?: string;
    onClick: () => void;
  };
}

/* ────────────────────────────────────────────────
   Skeleton backgrounds per page type
   ──────────────────────────────────────────────── */

function DashboardSkeleton() {
  return (
    <div className="space-y-6 pointer-events-none select-none">
      {/* KPI row */}
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-card rounded-[4px] border border-border p-4 space-y-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-7 w-24" />
            <Skeleton className="h-2 w-12" />
          </div>
        ))}
      </div>
      {/* Chart area */}
      <div className="bg-card rounded-[4px] border border-border p-6 space-y-4">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-[180px] w-full rounded" />
      </div>
      {/* Table rows */}
      <div className="bg-card rounded-[4px] border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-20 ml-auto" />
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="px-4 py-3 border-b border-border/50 flex items-center gap-6">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-20 ml-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pointer-events-none select-none">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="bg-card border border-border/50 rounded-[3px] overflow-hidden">
          <Skeleton className="aspect-square w-full" />
          <div className="p-3 space-y-2">
            <Skeleton className="h-3.5 w-3/4" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function RankTrackerSkeleton() {
  return (
    <div className="space-y-4 pointer-events-none select-none">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-card rounded-[4px] border border-border p-4 space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-6 w-16" />
          </div>
        ))}
      </div>
      {/* Table */}
      <div className="bg-card rounded-[4px] border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-4">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-7 w-24 ml-auto rounded-[10px]" />
        </div>
        {/* Header row */}
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-6 bg-muted/30">
          <Skeleton className="h-3 w-8" />
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-16 ml-auto" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="px-4 py-3 border-b border-border/50 flex items-center gap-6">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-3 w-36" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-8 w-20 rounded" />
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-12 ml-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}

function SearchTermsSkeleton() {
  return (
    <div className="space-y-4 pointer-events-none select-none">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="bg-card rounded-[4px] border border-border p-5 space-y-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 rounded-lg" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-8 w-28 rounded-[10px]" />
          </div>
          <div className="grid grid-cols-5 gap-2">
            {Array.from({ length: 5 }).map((_, j) => (
              <Skeleton key={j} className="h-8 rounded" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function BrandSignalsSkeleton() {
  return (
    <div className="space-y-6 pointer-events-none select-none">
      {/* Stats cards */}
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-card rounded-[4px] border border-border p-4 flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="space-y-1.5">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
      {/* Table */}
      <div className="bg-card rounded-[4px] border border-border overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-6 bg-muted/30">
          <Skeleton className="h-3 w-36" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-16 ml-auto" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="px-4 py-3 border-b border-border/50 flex items-center gap-6">
            <Skeleton className="h-3 w-44" />
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-3 w-14" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-5 w-5 rounded ml-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}

function GenericSkeleton() {
  return (
    <div className="space-y-4 pointer-events-none select-none">
      <div className="bg-card rounded-[4px] border border-border overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-6">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-16 ml-auto" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="px-4 py-3 border-b border-border/50 flex items-center gap-6">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-20 ml-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}

const skeletonMap: Record<SkeletonVariant, React.FC> = {
  dashboard: DashboardSkeleton,
  products: ProductsSkeleton,
  "rank-tracker": RankTrackerSkeleton,
  "search-terms": SearchTermsSkeleton,
  "brand-signals": BrandSignalsSkeleton,
  generic: GenericSkeleton,
};

export function EmptyBrandState({ pageName, description, showLandscapeCta = false, skeleton = "generic", keywordCta }: EmptyBrandStateProps) {
  const { selectedBrandId, brands } = useAppData();
  const { data: heroProducts = [], isPending: isHeroProductListPending } = productApi.useGetHeroProductList(
    selectedBrandId ?? undefined,
  );
  const { mutateAsync: addHeroProductsAsync } = productApi.useAddHeroProducts();
  const hasProducts = heroProducts.length > 0;
  const [addHeroAsinModalOpen, setAddHeroAsinModalOpen] = useState(false);
  const [addLandscapeOpen, setAddLandscapeOpen] = useState(false);

  const brandName = brands.find((b) => b.id === selectedBrandId)?.name ?? "this brand";

  const handleAddHeroAsins = async (asins: string[]) => {
    if (!selectedBrandId) return;
    try {
      const result = await addHeroProductsAsync({ brandId: selectedBrandId, asins });
      toast(productApi.describeAddHeroProductsResult(result));
    } catch (error) {
      handleApiError(error);
    }
  };

  const SkeletonComponent = skeletonMap[skeleton];
  const useKeywordCta = hasProducts && keywordCta != null;
  const isAwaitingHeroList = selectedBrandId != null && isHeroProductListPending;

  const resolvedDescription =
    description ??
    (useKeywordCta
      ? keywordCta.description ?? `Add keywords to start tracking ${pageName.toLowerCase()} data for ${brandName}.`
      : `Get started by adding Hero ASINs to ${brandName}. Once added, ${pageName.toLowerCase()} data will appear here.`);

  if (isAwaitingHeroList) {
    return (
      <div className="relative" aria-busy="true" aria-label="Loading brand data">
        <div className="pointer-events-none select-none">
          <SkeletonComponent />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Skeleton background — faded */}
      <div className="opacity-[0.35] blur-[0.5px]" aria-hidden="true">
        <SkeletonComponent />
      </div>

      {/* Overlay CTA */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex flex-col items-center text-center bg-card/90 backdrop-blur-sm rounded-xl px-10 py-8 shadow-lg border border-border/50 max-w-md">
          <div className="rounded-full bg-muted p-4 mb-4">
            <PackageOpen className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">
            No data yet for {brandName}
          </h2>
          <p className="text-sm text-muted-foreground max-w-md mb-6">
            {resolvedDescription}
          </p>
          <div className="flex items-center gap-3">
            {useKeywordCta ? (
              <Button onClick={keywordCta.onClick} className="gap-2">
                <Plus className="h-4 w-4" />
                {keywordCta.label ?? "Add Keyword"}
              </Button>
            ) : (
              <Button onClick={() => setAddHeroAsinModalOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Hero ASIN
              </Button>
            )}
            {showLandscapeCta && hasProducts && (
              <Button variant="outline" onClick={() => setAddLandscapeOpen(true)} className="gap-2">
                <Layers className="h-4 w-4" />
                Create Keyword Landscape
              </Button>
            )}
          </div>
        </div>
      </div>

      <AddHeroAsinModal
        open={addHeroAsinModalOpen}
        onOpenChange={setAddHeroAsinModalOpen}
        onAddAsins={handleAddHeroAsins}
      />
      {showLandscapeCta && hasProducts && (
        <AddLandscapeModal
          open={addLandscapeOpen}
          onOpenChange={setAddLandscapeOpen}
          onAdded={() => {}}
        />
      )}
    </div>
  );
}
